<template>
  <div style="display: flex; flex-direction: column; gap: 30px">
    <div>
      <h2>Endpoints públicos</h2>
      <p style="margin-top: 8px">Listado de endpoints públicos disponibles en el sistema</p>
    </div>

    <div class="endpoints-table">
      <el-table
        :data="endpointsList"
        border
        style="width: 950px"
        empty-text="No hay endpoints disponibles"
      >
        <el-table-column
          prop="Nombre"
          label="Nombre"
          width="200"
        />
        <el-table-column
          prop="Descripcion"
          label="Descripción"
          width="500"
        />
        <el-table-column
          prop="Url"
          label="URL"
          width="250"
        />
      </el-table>
    </div>

    <hr />

    <div class="api-keys-section">
      <div class="section-header">
        <div>
          <h2>Gestión de API Keys</h2>
          <p style="margin-top: 8px">Administre las claves de acceso para los endpoints públicos</p>
        </div>
        <el-button
          type="primary"
          :icon="IconAdd"
          @click="openCreateModal"
        >
          Agregar API Key
        </el-button>
      </div>
      <div class="api-keys-table">
        <el-table
          :data="apiKeys"
          border
          style="width: 950px"
          empty-text="No hay API Keys generadas"
          :height="200"
        >
          <el-table-column
            prop="Descripcion"
            label="Descripción"
            min-width="400"
          />
          <el-table-column
            label="Acciones"
            width="150"
            align="center"
          >
            <template #default="{ row }">
              <el-button
                :icon="IconEye"
                size="small"
                @click="openViewModal(row.Codigo)"
              />
              <el-button
                :icon="IconDelete"
                size="small"
                type="danger"
                @click="handleDelete(row)"
              />
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <modal-create-api-key
      v-if="createModalVisible"
      :visible="createModalVisible"
      @close="createModalVisible = false"
      @created="firstRequest"
    />

    <modal-view-api-key
      v-if="viewModalVisible"
      :visible="viewModalVisible"
      :api-key-id="selectedApiKeyId"
      @close="viewModalVisible = false"
    />
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
} from 'vue';
import { storeToRefs } from 'pinia';
import { ElMessageBox } from 'element-plus';
import api from '@/services/api';
import { useApiKeysStore } from './apiKeys_store';
import { useMessageError } from '@/shared/composables/message_error.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';
import { useLoading } from '@/shared/composables/loading.js';
import IconAdd from '~icons/mdi/plus';
import IconEye from '~icons/mdi/eye';
import IconDelete from '~icons/mdi/delete';
import ModalCreateApiKey from './modal_create_api_key.vue';
import ModalViewApiKey from './modal_view_api_key.vue';

const endpointsList = ref([]);
const createModalVisible = ref(false);
const viewModalVisible = ref(false);
const selectedApiKeyId = ref(null);

const apiKeysStore = useApiKeysStore();
const { apiKeys } = storeToRefs(apiKeysStore);

const fetchEndpoints = async () => {
  try {
    const response = await api.get('/endpoints');
    endpointsList.value = response.data;
  } catch (error) {
    useMessageError().open(error);
  }
};

const openCreateModal = () => {
  apiKeysStore.resetApiKey();
  createModalVisible.value = true;
};

const openViewModal = (codigo) => {
  selectedApiKeyId.value = codigo;
  viewModalVisible.value = true;
};

const firstRequest = async () => {
  apiKeysStore.emptyList();
  apiKeysStore.setPage(1);
  await apiKeysStore.findAllApiKeysHTTP({ page: 1 });
};

const handleDelete = async (row) => {
  try {
    if (row.Codigo) {
      await ElMessageBox.confirm(
        `¿Está seguro de eliminar la API Key "${row.Descripcion}"?`,
        'Confirmar eliminación',
        {
          confirmButtonText: 'Eliminar',
          cancelButtonText: 'Cancelar',
          type: 'warning',
        },
      );

      useLoading().openLoading();
      await apiKeysStore.removeApiKeyHTTP(row.Codigo);
      useMessageSuccess().open('API Key eliminada exitosamente');
      await firstRequest();
    }
  } catch (error) {
    if (error !== 'cancel') {
      useMessageError().open(error);
    }
  } finally {
    useLoading().closeLoading();
  }
};

onMounted(async () => {
  try {
    useLoading().openLoading();
    await Promise.all([fetchEndpoints(), apiKeysStore.firstRequest()]);
  } catch (error) {
    useMessageError().open(error);
  } finally {
    useLoading().closeLoading();
  }
});
</script>

<style scoped>
.endpoints-table,
.api-keys-table {
  background-color: white;
  border-radius: 4px;
}

.api-keys-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}
</style>
